import { useQuery } from '@tanstack/react-query';
import { DrawerLink } from '../../app/DrawerLink';
import { Link, useSearchParams } from 'react-router-dom';

import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import type { TrackSummary } from '../tracks/TrackList';

interface ChartTrack extends TrackSummary {
  rank: number;
  prev_rank: number | null;
  /** Positive is upward. Null means new to this chart. */
  movement: number | null;
  score: number;
}

interface Chart {
  chart: string;
  window: string;
  genre: string | null;
  computed_at: string | null;
  items: ChartTrack[];
}

interface Genre {
  id: number;
  name: string;
  slug: string;
}

/**
 * The windows, in the order they are offered.
 *
 * Trending first because it is the one that answers "what is happening now",
 * which is what somebody opening a discovery page is asking. The totals are
 * there for the artist checking their position, which is a different visit.
 */
const WINDOWS = [
  { id: 'trending', label: 'Trending' },
  { id: 'top24h', label: 'Today' },
  { id: 'top7d', label: 'This week' },
  { id: 'top30d', label: 'This month' },
  { id: 'topall', label: 'All time' },
] as const;

/**
 * Discover: the charts (HANDOFF §5).
 *
 * Reads a snapshot the cron computed, never a live aggregate - so two people
 * looking a minute apart see the same thing, which is what makes the movement
 * arrows mean anything at all.
 *
 * The window and genre live in the URL rather than in component state, because
 * "look at this week's house chart" is a thing somebody sends to a friend.
 */
export function DiscoverPage() {
  const [params, setParams] = useSearchParams();

  // Not named `window`: that shadows the global inside this component, and the
  // next person to reach for `window.scrollTo` gets a baffling error.
  const chartWindow = params.get('window') ?? 'trending';
  const genre = params.get('genre');

  const { data: genres } = useQuery({
    queryKey: ['genres'],
    queryFn: () => api.get<{ items: Genre[] }>('/genres'),
    staleTime: 60 * 60 * 1000,
  });

  const { data, isPending, isError } = useQuery<Chart>({
    queryKey: ['chart', chartWindow, genre],
    queryFn: () => api.get<Chart>(`/charts/${chartWindow}${genre ? `?genre=${genre}` : ''}`),
    retry: false,
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);

    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{copy['nav.discover']}</h1>
      <p className="mt-1.5 text-sm text-soft">
        {chartWindow === 'trending'
          ? 'What is moving right now — plays and favourites, weighted against how long a track has been out.'
          : 'Most played, by window.'}
      </p>

      {/* --------------------------------------------------------------- */}
      {/* Which chart                                                      */}
      {/* --------------------------------------------------------------- */}
      <div role="tablist" className="mt-6 flex flex-wrap gap-1 border-b border-line pb-3">
        {WINDOWS.map((option) => (
          <button
            key={option.id}
            role="tab"
            type="button"
            aria-selected={chartWindow === option.id}
            data-window={option.id}
            onClick={() => setParam('window', option.id)}
            className={`rounded-[var(--radius-pill)] px-4 py-2 text-sm transition-colors ${
              chartWindow === option.id ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <GenrePill active={genre === null} onClick={() => setParam('genre', null)}>
          Everything
        </GenrePill>
        {(genres?.items ?? []).map((option) => (
          <GenrePill
            key={option.id}
            active={genre === option.slug}
            onClick={() => setParam('genre', option.slug)}
          >
            {option.name}
          </GenrePill>
        ))}
      </div>

      {/* --------------------------------------------------------------- */}
      {/* The chart                                                        */}
      {/* --------------------------------------------------------------- */}
      {isPending ? (
        <div className="mt-6 flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : isError || !data ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">That chart could not be loaded.</p>
        </Card>
      ) : data.items.length === 0 ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">
            Nothing here yet. A track needs a handful of separate listeners before it can chart —
            which is what stops anybody putting themselves at the top.
          </p>
        </Card>
      ) : (
        <>
          <ChartList chart={data} />
          {data.computed_at && (
            /*
              Said plainly, because the page is not live and pretending
              otherwise would make somebody refresh it waiting for a number
              to move.
            */
            <p className="mt-4 text-xs text-faint">
              Updated {relative(data.computed_at)}. Charts are recomputed every hour.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ChartList({ chart }: { chart: Chart }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  return (
    <ol className="mt-6 flex flex-col gap-2" data-chart={chart.chart}>
      {chart.items.map((track) => {
        const isCurrent = current?.id === track.id;

        return (
          <li key={track.id} data-chart-row={track.id} data-rank={track.rank}>
            <Card className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              <div className="flex w-10 shrink-0 flex-col items-center">
                <span className="text-sm font-semibold tabular-nums text-ink">{track.rank}</span>
                <Movement movement={track.movement} />
              </div>

              <button
                type="button"
                aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                onClick={() => (isCurrent ? toggle() : void play(track, chart.items, 'chart'))}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
              >
                {isCurrent && playing ? (
                  <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                    <rect x="4" y="2.5" width="3" height="11" rx="1" />
                    <rect x="9" y="2.5" width="3" height="11" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
                    <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                  </svg>
                )}
              </button>

              {track.artwork_small ? (
                <img
                  src={track.artwork_small}
                  alt=""
                  loading="lazy"
                  className="hidden h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover sm:block"
                />
              ) : (
                <div
                  aria-hidden
                  className="hidden h-11 w-11 shrink-0 rounded-[var(--radius-control)] sm:block"
                  style={{ backgroundImage: 'repeating-linear-gradient(45deg, #151530 0 6px, #101024 6px 12px)' }}
                />
              )}

              <div className="min-w-0 flex-1">
                <DrawerLink
                  to={`/tracks/${track.id}`}
                  className="block truncate text-sm font-medium text-ink hover:underline"
                >
                  {track.title}
                </DrawerLink>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {track.owner && (
                    <Link to={`/${track.owner.username}`} className="meta-caps hover:text-ink">
                      {track.owner.display_name || `@${track.owner.username}`}
                    </Link>
                  )}
                  <span className="meta-caps">
                    {track.play_count.toLocaleString()} {track.play_count === 1 ? 'play' : 'plays'}
                  </span>
                  {track.movement === null && <Chip tone="fresh">{copy['label.newHere']}</Chip>}
                </div>
              </div>

              <FavoriteButton
                trackId={track.id}
                favorited={track.favorited}
                likeCount={track.like_count}
                size="sm"
              />
            </Card>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * ▲7 / ▼3 / — , or nothing at all for a new entry.
 *
 * §5 calls this a small detail with disproportionate pull, and it is right:
 * artists check their position obsessively and the delta is what makes a chart
 * feel alive rather than like a table.
 *
 * The arrow carries a text label for screen readers, because a triangle and a
 * number is not a sentence.
 */
function Movement({ movement }: { movement: number | null }) {
  if (movement === null) {
    return <span className="sr-only">New entry</span>;
  }

  if (movement === 0) {
    return (
      <span className="meta-caps text-faint" aria-label="No change">
        —
      </span>
    );
  }

  const up = movement > 0;

  return (
    <span
      className={`meta-caps tabular-nums ${up ? 'text-positive' : 'text-negative'}`}
      aria-label={`${up ? 'Up' : 'Down'} ${Math.abs(movement)}`}
    >
      <span aria-hidden="true">
        {up ? '▲' : '▼'}
        {Math.abs(movement)}
      </span>
    </span>
  );
}

function GenrePill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-[var(--radius-pill)] border px-3 py-1.5 text-xs transition-colors ${
        active
          ? 'border-line-lit bg-raised text-ink'
          : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** "20 minutes ago". Enough precision for something that updates hourly. */
function relative(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);

  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}
